import { useState } from 'react';

import { loadActions, loaders } from 'entities';

import { IconButton, Modal, Row, Select } from 'components/Blocks';
import { ExpandIcon, InstagramIcon } from 'components/Blocks/Icons';
import { PrivacyPolicyLink, UseTermsLink } from 'components/Links';

import { appConfig } from 'config/app';
import { env } from 'config/env';
import { LANG_CONFIG, useLang } from 'locale';

import { useUser } from 'providers/Auth';
import { useIsRoute } from 'routes';
import { cn } from 'styles';

import { COUNTRY_CONFIGS } from 'utils/constants';
import { joinUrl } from 'utils/url';

const linkClassName = 'hover:underline opacity-70 transition-opacity hover:opacity-100';

export function Footer() {
  const { t, lang, changeLanguage } = useLang();

  const { country } = useUser();

  const isHome = useIsRoute('home');

  const countryConfig = COUNTRY_CONFIGS[country];

  const [fingerprint, { retry, loadingThroughCache }] = loaders.infra.fingerprint.useV2({
    refreshMinutes: 5,

    refetchOnWindowFocus: 'always',
  });

  const [serverFingerprint, setServerFingerprint] = useState<any>(null);

  const syncFingerprint = loadActions.infra.syncFingerprint.use({
    block: !fingerprint,

    onSuccess: setServerFingerprint,

    params: fingerprint?.generated ?? {},
  });

  return (
    <footer
      className={cn(
        'mt-auto flex h-14 items-center justify-center gap-10 bg-background px-4 text-sm font-medium',
        'small_tablet:h-auto small_tablet:flex-col small_tablet:gap-4 small_tablet:py-4 ',
        // temporary fix for the progress strip
        isHome && 'small_tablet:pb-24',

        //
        'relative h-20',
      )}
    >
      <a
        href={appConfig.instagramUrl}
        target="instagram"
        className="opacity-50 transition-opacity hover:opacity-100"
      >
        <InstagramIcon size={24} />
      </a>

      <a href={env.supportUrl} target="_faq" className={linkClassName}>
        {t('faq')}
      </a>

      <PrivacyPolicyLink className={linkClassName} />

      <UseTermsLink className={linkClassName} />

      {countryConfig?.pqrPolicy && (
        <a href={joinUrl(env.mailUrl, '/policies/pqr')} target="pqr" className={linkClassName}>
          PQR
        </a>
      )}

      {countryConfig?.paymentPolicy && (
        <a
          href={joinUrl(env.mailUrl, '/policies/payment')}
          target="payment"
          className={linkClassName}
        >
          {t('payment-policy')}
        </a>
      )}

      {countryConfig?.cookiePolicy && (
        <a
          href={joinUrl(env.mailUrl, '/policies/cookies')}
          target="cookies"
          className={linkClassName}
        >
          {t('cookie-policy')}
        </a>
      )}

      {(countryConfig?.languages?.length as number) > 1 &&
        countryConfig!.languages?.includes(lang) && (
          <Row gap="2">
            <Select
              size="small"
              isSearchable={false}
              value={lang}
              onValueChange={changeLanguage}
              options={countryConfig!.languages!.map((lang) => ({
                value: lang,
                label: (
                  <Row>
                    <img className="size-5" src={LANG_CONFIG[lang].img} alt={lang} /> {t(lang)}
                  </Row>
                ),
              }))}
            />
          </Row>
        )}

      {!!fingerprint && (
        <Row
          justifyContent="space-between"
          className="absolute bottom-1 w-full px-2 text-xxs"
          gap="0"
        >
          <p>{fingerprint.saved}</p>

          <Row>
            <p onClick={loadingThroughCache ? undefined : retry} className="cursor-pointer">
              {loadingThroughCache
                ? 'Loading...'
                : fingerprint.saved === fingerprint.generated.id
                  ? 'STABLE'
                  : 'DIVERGENT'}
            </p>

            <IconButton className="p-1" {...syncFingerprint.buttonProps}>
              <ExpandIcon size={10} />
            </IconButton>
          </Row>

          <p>{fingerprint.generated.id}</p>
        </Row>
      )}

      <Modal
        isOpen={!!serverFingerprint}
        close={() => setServerFingerprint(null)}
        allowEscape={false}
        title="Full Detection"
        className="max-w-[1560px] overflow-y-auto"
      >
        <pre className="rounded-lg border bg-muted p-2">
          {JSON.stringify(serverFingerprint, null, 2)}
        </pre>
      </Modal>
    </footer>
  );
}
